import React, { useState } from 'react';
import { C } from '../../shared/design-tokens.js';
import { useFleetReadModel } from './use-fleet-read-model.js';
import FleetContextRail from './FleetContextRail.jsx';
import FleetVehicleRegistry from './FleetVehicleRegistry.jsx';
import FleetIssuesPanel from './FleetIssuesPanel.jsx';
import FleetVehicleDetailDrawer from './FleetVehicleDetailDrawer.jsx';
import FleetPendingTransfers from './FleetPendingTransfers.jsx';
import FleetRentalReadSeam from './FleetRentalReadSeam.jsx';
import FleetAddVehicle from './FleetAddVehicle.jsx';
import Drawer from '../../shared/ui/Drawer.js';

export default function FleetModule() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [terminalFilter, setTerminalFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | number | null>(null);

  const [transferRefreshKey, setTransferRefreshKey] = useState(0);

  const [showAdd, setShowAdd] = useState(false);
  const [showTransfers, setShowTransfers] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [showRentals, setShowRentals] = useState(false);

  const {
    vehicles,
    terminalOptions,
    statusCounts,
    vehiclesWithIssues,
    totalOpenIssues,
    loading,
    error,
    refreshFleet,
  } = useFleetReadModel(statusFilter, terminalFilter);

  function handleTransferComplete() {
    setTransferRefreshKey((k) => k + 1);
    refreshFleet();
  }

  const rentalCount = vehicles.filter((v) => v.lease_type === 'rental').length;

  function closeAdd() { setShowAdd(false); }
  function closeTransfers() { setShowTransfers(false); }
  function closeIssues() { setShowIssues(false); }
  function closeRentals() { setShowRentals(false); }

  return (
    <div>
      <FleetContextRail
        statusFilter={statusFilter}
        terminalFilter={terminalFilter}
        terminalOptions={terminalOptions}
        statusCounts={statusCounts}
        onStatusChange={setStatusFilter}
        onTerminalChange={setTerminalFilter}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowAdd(true)}
          disabled={loading}
        >
          + Add vehicle
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowTransfers(true)}
        >
          Pending transfers
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowIssues(true)}
        >
          Open issues
          {totalOpenIssues > 0 ? (
            <span style={{ marginLeft: 6, color: C.gold, fontWeight: 700 }}>· {totalOpenIssues}</span>
          ) : null}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setShowRentals(true)}
        >
          Rentals ({rentalCount})
        </button>
      </div>

      {loading ? <div style={{ color: C.textMuted, marginTop: 16 }}>Loading fleet...</div> : null}
      {error ? <div style={{ color: C.red, marginTop: 16 }}>Fleet load failed: {error}</div> : null}

      {!loading && !error ? (
        <FleetVehicleRegistry
          vehicles={vehicles}
          search={search}
          onSearchChange={setSearch}
          onOpenVehicle={setSelectedVehicleId}
        />
      ) : null}

      <FleetVehicleDetailDrawer
        vehicleId={selectedVehicleId}
        onClose={() => setSelectedVehicleId(null)}
        terminalOptions={terminalOptions}
        onTransferComplete={handleTransferComplete}
      />

      <Drawer open={showAdd} onClose={closeAdd} title="Add vehicle" width={560}>
        <FleetAddVehicle
          terminalOptions={terminalOptions}
          onSuccess={() => { refreshFleet(); closeAdd(); }}
        />
      </Drawer>

      <Drawer open={showTransfers} onClose={closeTransfers} title="Pending transfers" width={560}>
        <FleetPendingTransfers
          refreshKey={transferRefreshKey}
          onReceiveComplete={handleTransferComplete}
          onCancelComplete={handleTransferComplete}
        />
      </Drawer>

      <Drawer open={showIssues} onClose={closeIssues} title="Open issues" width={600}>
        <FleetIssuesPanel
          vehiclesWithIssues={vehiclesWithIssues}
          totalOpenIssues={totalOpenIssues}
        />
      </Drawer>

      <Drawer open={showRentals} onClose={closeRentals} title="Rental vehicles" width={560}>
        <FleetRentalReadSeam vehicles={vehicles} />
      </Drawer>
    </div>
  );
}
